import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, MapPin, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import PageMeta from '../components/PageMeta';
import OtpVerifyStep from '../components/OtpVerifyStep';

const LoginPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [needs2FA, setNeeds2FA] = useState(false);
  const [tempToken, setTempToken] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setNeedsVerification(false);
    
    try {
      const result = await login(email, password);
      if (result.requires_2fa) {
        setNeeds2FA(true);
        setTempToken(result.temp_token);
        setLoading(false);
        return;
      }
      toast.success('Welcome back!');
      const redirect = searchParams.get('redirect');
      navigate(redirect || '/dashboard');
    } catch (error) {
      const detail = error.response?.data?.detail || 'Invalid credentials';
      if (detail.includes('verify your email')) {
        setNeedsVerification(true);
      }
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email) { toast.error('Enter your email first'); return; }
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API}/api/auth/resend-verification`, {
        method: 'POST', headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({ email })
      });
      if (res.ok) {
        toast.success('Verification email sent! Check your inbox.');
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to resend');
      }
    } catch { toast.error('Failed to resend'); }
  };

  const handle2FAVerify = async (code, trustDevice, trustDays) => {
    setLoading(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API}/api/auth/2fa/verify-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ temp_token: tempToken, code, trust_device: trustDevice, trust_days: trustDays })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Verification failed');
      
      // Store tokens
      localStorage.setItem('gmb_token', data.access_token);
      if (data.trusted_device_token) {
        localStorage.setItem('trusted_device_token', data.trusted_device_token);
      }
      toast.success('Welcome back!');
      window.location.href = searchParams.get('redirect') || '/dashboard';
    } catch (err) {
      toast.error(err.message || 'Invalid code');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-white flex">
      <PageMeta title="Sign In - GMB Autopilot" />
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md animate-fade-in-up">
          <Link to="/" className="inline-flex items-center gap-2 mb-8">
            <div className="w-8 h-8 bg-[#1A73E8] rounded-lg flex items-center justify-center">
              <MapPin className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>
              GMB Autopilot
            </span>
          </Link>

          {needs2FA ? (
            <OtpVerifyStep
              onVerify={handle2FAVerify}
              onCancel={() => { setNeeds2FA(false); setTempToken(''); }}
              loading={loading}
            />
          ) : (
          <>
          <h1 className="text-3xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>
            Welcome back
          </h1>
          <p className="mt-2 text-[#5F6368]">
            Sign in to continue to your dashboard
          </p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5">
            <div>
              <label className="block text-sm font-medium text-[#202124] mb-2">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-google"
                placeholder="you@example.com"
                required
                data-testid="login-email-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-[#202124] mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-google pr-12"
                  placeholder="Enter your password"
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5F6368] hover:text-[#202124]"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-google w-full flex items-center justify-center gap-2"
              data-testid="login-submit-btn"
            >
              {loading ? 'Signing in...' : 'Sign In'}
              {!loading && <ArrowRight className="w-5 h-5" />}
            </button>
          </form>

          <div className="mt-4 text-center">
            <Link to="/forgot-password" className="text-sm text-[#5F6368] hover:text-[#1A73E8] hover:underline" data-testid="forgot-password-link">
              Forgot your password?
            </Link>
          </div>

          {needsVerification && (
            <div className="mt-4 p-4 bg-[#FEF7E0] border border-[#F9AB00] rounded-lg text-center" data-testid="verification-notice">
              <p className="text-sm text-[#5F6368] mb-2">Your email is not verified yet.</p>
              <button onClick={handleResendVerification} className="text-sm text-[#1A73E8] font-medium hover:underline" data-testid="resend-verification-login-btn">
                Resend verification email
              </button>
            </div>
          )}

          <p className="mt-6 text-center text-[#5F6368]">
            Don't have an account?{' '}
            <Link to="/register" className="text-[#1A73E8] font-medium hover:underline">
              Sign up free
            </Link>
          </p>
          </>
          )}
        </div>
      </div>

      {/* Right Side - Visual */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#F8F9FA] items-center justify-center p-12">
        <div className="max-w-md text-center">
          <div className="w-24 h-24 mx-auto bg-[#E8F0FE] rounded-2xl flex items-center justify-center mb-8">
            <MapPin className="w-12 h-12 text-[#1A73E8]" />
          </div>
          <h2 className="text-2xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>
            Optimize Your Google Business Profile
          </h2>
          <p className="mt-4 text-[#5F6368]">
            AI-powered tools to create posts, manage reviews, and grow your local presence.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
